import { api } from '../api';
import { Bars, LineChart, StackBar } from '../components/Charts';
import { Bar, Empty, ErrorBox, Icon, Loading, Metric, PageHead, Panel } from '../components/ui';
import { dateLabel, dueLabel, fixed, pct, pending, shortDate } from '../format';
import { useApi } from '../hooks';
import { KIND } from '../labels';
import type { Dashboard as DashboardData } from '../types';
import { StatusBadge } from './Decisions';
import { GoalRow } from './Goals';
import { stepDetail, stepLabel } from './Train';

export function Dashboard() {
  const { data, error, reload } = useApi(api.dashboard);
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const left = data.tasks.filter((t) => !t.done).length;
  return (
    <>
      <PageHead
        title="오늘의 APEX"
        sub={`${dateLabel(data.today)}${data.streak > 0 ? ` · ${data.streak}일 연속 복습` : ''}`}
        actions={
          <>
            <a className="btn" href="#/knowledge/new"><Icon name="plus" size={16} />새 개념</a>
            <a className="btn btn-primary" href="#/train">훈련 시작</a>
          </>
        }
      />
      <div className="dash">
        <TrainingHero />
        <Panel title="기억·판단 할 일" className="span-2" action={<span className="muted small">{left ? `${left}개 남음` : '모두 완료'}</span>}>
          <ul className="tasks">
            {data.tasks.map((t) => (
              <li key={t.key} className={t.done ? 'done' : ''}>
                <a href={t.href}>
                  <span className="task-check" aria-hidden="true">{t.done && <Icon name="check" size={14} />}</span>
                  <span className="task-body">
                    <span className="task-label">{t.label}</span>
                    <span className="task-detail">{t.detail}</span>
                  </span>
                  <Icon name="arrow" size={16} />
                </a>
              </li>
            ))}
          </ul>
        </Panel>
        <ProgressPanel d={data} />
        <TrendPanel d={data} />
        <WeakPanel d={data} />
        <DuePanel d={data} />
        <DecisionsPanel d={data} />
        <GoalsPanel d={data} />
      </div>
    </>
  );
}

function TrainingHero() {
  const { data, error, reload } = useApi(api.trainingToday);
  if (error) return <div className="span-2"><ErrorBox message={error} onRetry={reload} /></div>;
  if (!data) return null;
  const started = data.steps.some((s) => s.done);
  return (
    <Panel title="오늘 훈련" className="span-2 train-hero" action={<span className="muted small">{data.done ? '모두 완료' : `약 ${data.minutes_left}분`}</span>}>
      <ol className="hero-steps">
        {data.steps.map((s) => (
          <li key={s.key} className={s.done ? 'done' : ''}>
            <span className="task-check" aria-hidden="true">{s.done && <Icon name="check" size={14} />}</span>
            <span className="task-body">
              <span className="task-label">{stepLabel(s)}</span>
              <span className="task-detail">{stepDetail(s)}</span>
            </span>
          </li>
        ))}
      </ol>
      <a className="btn btn-primary big" href="#/train">
        {data.done ? '오늘 훈련 보기' : started ? '이어서 하기' : '훈련 시작'} <Icon name="arrow" size={16} />
      </a>
    </Panel>
  );
}

function ProgressPanel({ d }: { d: DashboardData }) {
  const p = d.progress;
  const total = p.today_done + p.today_left;
  const r = p.retention_30d;
  return (
    <Panel title="학습 진행률">
      <div className="row-between">
        <span>오늘 복습</span>
        <b>{p.today_done} / {total}</b>
      </div>
      <Bar value={total ? p.today_done / total : 0} tone="good" />
      <div className="metrics-2">
        <Metric
          label="30일 기억 유지율"
          value={pct(r.rate)}
          sub={`목표 ${pct(p.target)} · n=${r.n}`}
          insufficient={r.n < p.min_retention_n ? pending(r.n, p.min_retention_n, '회') : undefined}
        />
        <Metric
          label="90일 브라이어"
          value={fixed(p.brier_90d.value)}
          sub={`낮을수록 좋음 · n=${p.brier_90d.n}`}
          insufficient={p.brier_90d.n < p.min_pred_n ? pending(p.brier_90d.n, p.min_pred_n, '건') : undefined}
        />
      </div>
      <p className="small muted">지식 {p.items}개 · 카드 상태</p>
      <StackBar
        parts={[
          { label: '새 카드', value: p.cards.new, tone: 'c-new' },
          { label: '학습 중', value: p.cards.learning, tone: 'c-learn' },
          { label: '단기', value: p.cards.young, tone: 'c-young' },
          { label: '장기(21일+)', value: p.cards.mature, tone: 'c-mature' },
        ]}
      />
    </Panel>
  );
}

function TrendPanel({ d }: { d: DashboardData }) {
  const weekly = d.trend.retention_weekly;
  return (
    <Panel title="최근 성장 추세" className="span-2" action={<a className="link small" href="#/stats">자세히</a>}>
      <div className="trend-grid">
        <div>
          <h3 className="sub-title">주별 기억 유지율 (점선 = 목표)</h3>
          {weekly.some((w) => w.n >= 5) ? (
            <LineChart
              points={weekly.map((w) => ({ label: shortDate(w.week), value: w.rate, n: w.n }))}
              min={0.5} max={1} target={d.progress.target} format={(v) => pct(v)} minN={5} ariaLabel="주별 기억 유지율"
            />
          ) : (
            <Empty>복습 단계 카드의 기록이 주 5회 이상 쌓이면 그려집니다.</Empty>
          )}
        </div>
        <div>
          <h3 className="sub-title">하루 복습 수 (14일, 진한 부분 = '다시')</h3>
          <Bars data={d.trend.reviews_daily.map((x) => ({ label: shortDate(x.day), value: x.count, sub: x.again }))} ariaLabel="하루 복습 수" />
        </div>
        {d.trend.brier_rolling.length >= 3 && (
          <div className="span-all">
            <h3 className="sub-title">브라이어 점수 — 최근 10건 이동평균 (낮을수록 좋음, 점선 = 늘 50%)</h3>
            <LineChart
              points={d.trend.brier_rolling.map((x) => ({ label: shortDate(x.date), value: x.brier, n: x.n }))}
              min={0} max={0.5} target={0.25} format={(v) => v.toFixed(2)} ariaLabel="브라이어 이동평균"
            />
          </div>
        )}
      </div>
    </Panel>
  );
}

function WeakPanel({ d }: { d: DashboardData }) {
  return (
    <Panel title="취약한 능력">
      {d.weak.length === 0 ? (
        <Empty>{d.weak_note}</Empty>
      ) : (
        <ul className="weak-list">
          {d.weak.map((w) => (
            <li key={w.title} className={`weak ${w.area}`}>
              <a href={w.href}>
                <b>{w.title}</b>
                <span className="muted small">{w.detail}</span>
                <span className="small">→ {w.action}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function DuePanel({ d }: { d: DashboardData }) {
  return (
    <Panel title="복습할 지식" action={d.due_items.length > 0 && <a className="link small" href="#/review">복습하기</a>}>
      {d.due_items.length === 0 ? (
        <Empty>
          지금 때가 된 지식이 없습니다.{d.next_due && ` 다음 복습: ${dueLabel(d.next_due)}`}
        </Empty>
      ) : (
        <ul className="list">
          {d.due_items.map((it) => (
            <li key={it.item_id}>
              <a href={`#/knowledge/${it.item_id}`} className="list-row">
                <span className="grow">
                  <b>{it.title}</b>
                  <span className="muted small">{it.kinds.map((k) => KIND[k]).join(' · ')}</span>
                </span>
                <span className="badge">{it.recall != null ? `기억 ${pct(it.recall)}` : `${it.count}장`}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function DecisionsPanel({ d }: { d: DashboardData }) {
  return (
    <Panel title="최근 의사결정" action={<a className="link small" href="#/decisions/new">새 예측</a>}>
      {d.recent_decisions.length === 0 ? (
        <Empty>아직 예측 기록이 없습니다. 결과가 1~4주 안에 나오는 질문부터 시작해 보세요.</Empty>
      ) : (
        <ul className="list">
          {d.recent_decisions.map((p) => (
            <li key={p.id}>
              <a href={`#/decisions/${p.id}`} className="list-row">
                <span className="prob">{Math.round(p.prob_current * 100)}%</span>
                <span className="grow clamp-2">{p.question}</span>
                <StatusBadge p={p} />
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function GoalsPanel({ d }: { d: DashboardData }) {
  return (
    <Panel title="장기 목표" action={<a className="link small" href="#/goals">관리</a>}>
      {d.goals.length === 0 ? (
        <Empty>진행 중인 목표가 없습니다. 예: "3개월 안에 브라이어 0.18 이하", "지식 100개".</Empty>
      ) : (
        <ul className="goal-list">
          {d.goals.map((g) => <GoalRow key={g.id} g={g} />)}
        </ul>
      )}
    </Panel>
  );
}
